import { Button, Modal, Spin, Typography } from 'antd';
import { useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../utils/pdfWorker';
import PdfErrorBoundary from '../PdfErrorBoundary';
import { MaterialItem } from '../../types/materials';
import { materialFileUrl } from '../../api/materials';

interface MaterialViewerProps {
    material: MaterialItem | null;
    onClose: () => void;
}

// A modal PDF reader, same react-pdf recipe as OfferPage: every page rendered to container width, a
// spinner while pdf.js loads, and an error state that retries by remounting <Document> via reloadKey.
// `destroyOnClose` throws the whole PDF away when the modal closes so nothing lingers between opens.
const MaterialViewer = ({ material, onClose }: MaterialViewerProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [numPages, setNumPages] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    const fileUrl = materialFileUrl(material?.file_path ?? null);

    return (
        <Modal
            open={material !== null}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            width="92%"
            style={{ top: 16 }}
            title={material?.title}
            className="material-viewer"
        >
            <div
                className={`offer-frame offer-frame--large ${loading ? 'offer-frame--loading' : ''}`}
                ref={(node) => setContainerWidth(node?.clientWidth ?? 0)}
            >
                {loading && !error && (
                    <div className="offer-loader-overlay">
                        <Spin size="large" />
                    </div>
                )}
                {error ? (
                    <div className="offer-error">
                        <Typography.Text className="section-text">
                            Hujjatni yuklab bo'lmadi. Iltimos, qayta urinib ko'ring.
                        </Typography.Text>
                        <Button
                            onClick={() => {
                                setError(false);
                                setLoading(true);
                                setNumPages(0);
                                setReloadKey((key) => key + 1);
                            }}
                        >
                            Qayta urinish
                        </Button>
                    </div>
                ) : (
                    fileUrl && (
                      <PdfErrorBoundary
                        fallback={
                            <div className="offer-error">
                                <Typography.Text className="section-text">
                                    Hujjatni ko'rsatib bo'lmadi. Uni yuklab olib o'qishingiz mumkin.
                                </Typography.Text>
                                <Button href={fileUrl} target="_blank" rel="noreferrer">
                                    Yuklab olish
                                </Button>
                            </div>
                        }
                      >
                        <Document
                            key={reloadKey}
                            file={fileUrl}
                            onLoadSuccess={({ numPages: loadedPages }) => {
                                setNumPages(loadedPages);
                                setLoading(false);
                                setError(false);
                            }}
                            onLoadError={() => {
                                setLoading(false);
                                setError(true);
                            }}
                            className="offer-pdf-document"
                        >
                            {containerWidth > 0 &&
                                Array.from({ length: numPages }, (_, index) => (
                                    <Page key={index + 1} pageNumber={index + 1} width={containerWidth} />
                                ))}
                        </Document>
                      </PdfErrorBoundary>
                    )
                )}
            </div>
        </Modal>
    );
};

export default MaterialViewer;
